import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import Footer from "../../../../core/common/Footer";
import SearchableSelect from "../../../../core/common/SearchableSelect";
import useApi from "../../../../core/customhook/useApi";
import { displayValue, formatDate } from "../../../../core/common/TabelValue";
import Swal from "sweetalert2";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import useProjects from "../../../../core/customhook/useProjects";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import { getSearchableHeader } from "../../../utils/getSearchableHeader";
import { filterByColumnSearch } from "../../../utils/filterByColumnSearch";

const ProgramList = () => {
  const navigate = useNavigate();
  const { projects, loading } = useProjects();
  const [showGuide, setShowGuide] = useState<boolean>(true);
  const [itemList, setItemList] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 1000 });
  const [total, setTotal] = useState(0);

  const getProgram = useApi<any>(
    `/api/v1/master/projects/${selectedProjectId}/all-programs?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const programLoading = getProgram.loading;

  const updateProgramStatus = useApi<any>("", {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) return;
    const nextPage = limit !== pagination.limit ? 1 : page;
    setPagination({ page: nextPage, limit });
  };
  const fetchPrograms = async () => {
    if (!selectedProjectId) {
      setItemList([]);
      setTotal(0);
      return;
    }

    try {
      const res = await getProgram.callApi();

      if (res?.success) {
        setItemList(res.data?.table || []);
        setTotal(res.data?.pagination?.total ?? 0);
      } else {
        setItemList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error(error);
      setItemList([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [selectedProjectId, pagination.page, pagination.limit]);

  const handleStatusToggle = async (record: any) => {
    const previousStatus = record.is_active;

    setItemList((prev: any[]) =>
      prev.map((item) =>
        item.program_id === record.program_id
          ? {
              ...item,
              is_active: !previousStatus,
            }
          : item,
      ),
    );

    try {
      const payload = {
        is_active: !previousStatus,
      };

      const res = await updateProgramStatus.callApi(
        payload,
        `/api/v1/master/programs/${record.program_id}/status`,
      );

      if (!res?.success) {
        throw new Error(res?.message);
      }
    } catch (error: any) {
      // Revert UI if API fails
      setItemList((prev: any[]) =>
        prev.map((item) =>
          item.program_id === record.program_id
            ? {
                ...item,
                is_active: previousStatus,
              }
            : item,
        ),
      );

      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong while updating the status.",
      });
    }
  };

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});

  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
  );

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      width: 80,
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Program Name",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      sorter: (a, b) =>
        String(a.program_name ?? "").localeCompare(
          String(b.program_name ?? ""),
        ),
      render: displayValue,
      onCell: () => cellWidth(300),
    },
    {
      title: getSearchableHeader(
        "Line of Business",
        "lob",
        setInventoryColumnSearch,
      ),
      key: "lob",
      align: "center",
      render: (_: any, record: any) => (
        <div
          title={`${record.department_count ?? 0} linked lines of business`}
          style={{
            cursor: "pointer",
            color: "#f26522",
            textDecoration: "underline",
          }}
          className="text-decoration-none p-0"
          onClick={() =>
            navigate(all_routes.addLob, {
              state: {
                program_id: record.program_id,
                projectId: selectedProjectId,
              },
            })
          }
        >
          <span style={{ textDecoration: "underline" }}>
            Add Line of Business
          </span>
          <span
            className="ms-2 d-inline-flex align-items-center justify-content-center"
            style={{
              minWidth: "28px",
              height: "28px",
              padding: "0 8px",
              borderRadius: "999px",
              backgroundColor: "#f26522",
              color: "#fff",
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {record.department_count}
          </span>
        </div>
      ),
    },
    {
      title: getSearchableHeader(
        "Program Code",
        "program_code",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_code",
      key: "program_code",
      sorter: (a, b) =>
        String(a.program_code ?? "").localeCompare(
          String(b.program_code ?? ""),
        ),
      render: displayValue,
    },
    {
      title: getSearchableHeader(
        "Location",
        "location_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "location_name",
      key: "location_name",
      sorter: (a, b) =>
        String(a.location_name ?? "").localeCompare(
          String(b.location_name ?? ""),
        ),
      render: displayValue,
      onCell: () => cellWidth(180),
    },
    {
      title: getSearchableHeader(
        "Created On",
        "created_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => formatDate(value),
      sorter: (a, b) =>
        String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")),
    },
    {
      title: getSearchableHeader(
        "Last Updated",
        "updated_at",
        setInventoryColumnSearch,
      ),
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value) => formatDate(value),
      sorter: (a, b) =>
        String(a.updated_at ?? "").localeCompare(String(b.updated_at ?? "")),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={record.is_active}
            onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Program Setup</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Program Setup</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate(all_routes.addprogram)}
          >
            <i className="ti ti-plus"></i> Add Program
          </button>
          <button
            className="btn border bg-white px-3"
            onClick={() => setShowGuide((prev) => !prev)}
          >
            <i className="ti ti-info-circle-filled fs-16"></i> Guide
          </button>
        </div>

        {showGuide && (
          <div
            style={{
              overflow: "hidden",
              maxHeight: showGuide ? "500px" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            {/* Triangle Arrow */}
            <div className="text-end pe-3">
              <i
                className="ti ti-triangle-inverted-filled"
                style={{
                  fontSize: "22px",
                  color: "#cfcfcf",
                  lineHeight: 0,
                }}
              ></i>
            </div>

            {/* Guide Card */}
            <div
              className="card border-0 shadow-sm"
              style={{
                borderRadius: "4px",
              }}
            >
              <div className="card-body position-relative p-4">
                {/* Close Icon */}
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <button
                    type="button"
                    aria-label="Close guide"
                    className="border-0 p-0"
                    style={{ background: "transparent" }}
                    onClick={() => setShowGuide(false)}
                  >
                    <i
                      className="ti ti-x"
                      style={{
                        background: "#6c757d",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        fontSize: "12px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    ></i>
                  </button>
                </div>

                {/* Content */}
                <h5 className="fw-bold mb-3">Program Setup</h5>

                <ul
                  className="mb-0 ps-3 text-dark"
                  style={{ lineHeight: "1.8", listStyleType: "disc" }}
                >
                  <li>
                    Create and manage programs to organize candidate screening
                    requirements by job role, department, or location. Programs
                    help you assign appropriate screening packages and maintain
                    consistent verification processes.
                  </li>
                  <li>
                    For example, you can create separate programs for IT, BPO,
                    lateral hiring, freshers, or different office locations.
                  </li>
                  <li>
                    Select a brand to view and manage its programs. Configured
                    programs will be available for selection during candidate
                    onboarding.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="col-md-4">
          <SearchableSelect
            label="Brand Name"
            name="project_id"
            value={selectedProjectId}
            options={projects.map((item: any) => ({
              label: item.project_name,
              value: item.project_id,
            }))}
            onChange={(_, value) => {
              setSelectedProjectId(String(value ?? ""));
              setItemList([]);
              setTotal(0);
              setInventoryColumnSearch({});
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            hasRowGrid={false}
            required
          />
        </div>
        {!selectedProjectId && !loading && (
          <div className="card mt-3">
            <div className="card-body text-center py-5" role="status">
              <i
                className="ti ti-building fs-1 text-muted"
                aria-hidden="true"
              />
              <h5 className="mt-3 mb-2">Select a Brand to View Programs</h5>
              <p className="text-muted mb-0">
                Choose a brand from the dropdown above to view and manage its
                associated programs.
              </p>
            </div>
          </div>
        )}
        {/* Table */}
        {/* {selectedProjectId &&
          (programLoading ? (
            <SnipperLoader />
          ) : (
            <div className="card mt-3">  
              {filteredData.length === 0 && (
                <div className="text-center py-4" role="status">
                  <i className="ti ti-folder-off fs-1 text-muted" aria-hidden="true" />
                  <h5 className="mt-2">No Programs Found</h5>
                  <p className="text-muted mb-0">
                    {itemList.length === 0
                      ? "No programs are available for the selected brand."
                      : "No programs match your current search criteria."}
                  </p>
                </div>
              )}
              <Datatable
                dataSource={filteredData}
                columns={columns}
                Selection={false}
                fixedHeader
                tableHeight={650}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total,
                }}
                handleTableChange={(p: any) =>
                  handleTableChange(p.current, p.pageSize)
                }
              />
            </div>
          ))} */}

        {/* Program List */}
        {selectedProjectId &&
          (programLoading ? (
            <SnipperLoader />
          ) : itemList.length === 0 ? (
            // No programs available for selected brand
            <div className="card mt-3">
              <div
                className="card-body d-flex flex-column align-items-center justify-content-center text-center"
                style={{ minHeight: "200px" }}
              >
                <i
                  className="ti ti-folder-off text-secondary mb-2"
                  style={{ fontSize: "36px" }}
                />

                <h5 className="fw-semibold mb-1">No Programs Found</h5>

                <p className="text-muted mb-0">
                  No programs are available for the selected brand.
                </p>
              </div>
            </div>
          ) : (
            // Show table only when programs exist
            <div className="card mt-3">
              <Datatable
                dataSource={filteredData}
                columns={columns}
                Selection={false}
                fixedHeader
                tableHeight={650}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total,
                }}
                handleTableChange={(p: any) =>
                  handleTableChange(p.current, p.pageSize)
                }
              />
            </div>
          ))}
      </div>

      <Footer />
    </div>
  );
};

export default ProgramList;

// import { useEffect, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { all_routes } from "../../../router/all_routes";
// import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
// import Datatable from "../../../../core/common/dataTable";
// import { ColumnsType } from "antd/es/table";
// import Footer from "../../../../core/common/Footer";
// import SearchableSelect from "../../../../core/common/SearchableSelect";
// import useApi from "../../../../core/customhook/useApi";
// import { displayValue, formatDate } from "../../../../core/common/TabelValue";
// import Swal from "sweetalert2";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
// import useProjects from "../../../../core/customhook/useProjects";
// import { cellWidth } from "../../../../core/methods/tableCellWidth";
// import { getSearchableHeader } from "../../../utils/getSearchableHeader";
// import { filterByColumnSearch } from "../../../utils/filterByColumnSearch";

// const ProgramList = () => {
//   const navigate = useNavigate();
//   const { projects, loading } = useProjects();
//   const [showGuide, setShowGuide] = useState<boolean>(true);
//   const [itemList, setItemList] = useState<any[]>([]);
//   const [selectedProjectId, setSelectedProjectId] = useState("");
//   const [pagination, setPagination] = useState({ page: 1, limit: 1000 });
//   const [total, setTotal] = useState(0);

//   const getProgram = useApi<any>(
//     `/api/v1/master/projects/${selectedProjectId}/all-programs?page=${pagination.page}&limit=${pagination.limit}`,
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const programLoading = getProgram.loading;

//   const updateProgramStatus = useApi<any>("", {
//     method: "PATCH",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const handleTableChange = (
//     page: number,
//     limit: number = pagination.limit,
//   ) => {
//     if (page === pagination.page && limit === pagination.limit) return;
//     const nextPage = limit !== pagination.limit ? 1 : page;
//     setPagination({ page: nextPage, limit });
//   };
//   const fetchPrograms = async () => {
//     if (!selectedProjectId) return;

//     try {
//       const res = await getProgram.callApi();

//       if (res?.success) {
//         setItemList(res.data?.table || []);
//         setTotal(res.data?.pagination?.total ?? 0);
//       } else {
//         setItemList([]);
//       }
//     } catch (error) {
//       console.error(error);
//       setItemList([]);
//     }
//   };

//   useEffect(() => {
//     fetchPrograms();
//   }, [selectedProjectId, pagination.page, pagination.limit]);

//   const handleStatusToggle = async (record: any) => {
//     const previousStatus = record.is_active;

//     setItemList((prev: any[]) =>
//       prev.map((item) =>
//         item.program_id === record.program_id
//           ? {
//               ...item,
//               is_active: !previousStatus,
//             }
//           : item,
//       ),
//     );

//     try {
//       const payload = {
//         is_active: !previousStatus,
//       };

//       const res = await updateProgramStatus.callApi(
//         payload,
//         `/api/v1/master/programs/${record.program_id}/status`,
//       );

//       if (!res?.success) {
//         throw new Error(res?.message);
//       }
//     } catch (error: any) {
//       // Revert UI if API fails
//       setItemList((prev: any[]) =>
//         prev.map((item) =>
//           item.program_id === record.program_id
//             ? {
//                 ...item,
//                 is_active: previousStatus,
//               }
//             : item,
//         ),
//       );

//       console.error(error);

//       Swal.fire({
//         icon: "error",
//         title: "Update Failed",
//         text:
//           error?.response?.data?.message ||
//           error?.response?.data?.error ||
//           error?.message ||
//           "Something went wrong while updating the status.",
//       });
//     }
//   };

//   const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
//     Record<string, string>
//   >({});

//   const filteredData = filterByColumnSearch(
//     Array.isArray(itemList) ? itemList : [],
//     inventoryColumnSearch,
//   );

//   const columns: ColumnsType<any> = [
//     {
//       title: "Sr. No.",
//       key: "serial_number",
//       dataIndex: "serial_number",
//       width: 80,
//       align: "center",
//     },
//     {
//       title: getSearchableHeader(
//         "Program Name",
//         "program_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "program_name",
//       key: "program_name",
//       sorter: (a, b) => a.program_name.localeCompare(b.program_name),
//       render: displayValue,
//       onCell: () => cellWidth(300),
//     },
//     {
//       title: getSearchableHeader(
//         "Line of Business",
//         "lob",
//         setInventoryColumnSearch,
//       ),
//       key: "lob",
//       align: "center",
//       render: (_: any, record: any) => (
//         <div
//           style={{
//             cursor: "pointer",
//             color: "#f26522",
//             textDecoration: "underline",
//           }}
//           className="text-decoration-none p-0"
//           onClick={() =>
//             navigate(all_routes.addLob, {
//               state: {
//                 program_id: record.program_id,
//                 projectId: selectedProjectId,
//               },
//             })
//           }
//         >
//           <span style={{ textDecoration: "underline" }}>Add Line of Business</span>
//           <span
//             className="ms-2 d-inline-flex align-items-center justify-content-center"
//             style={{
//               minWidth: "28px",
//               height: "28px",
//               padding: "0 8px",
//               borderRadius: "999px",
//               backgroundColor: "#f26522",
//               color: "#fff",
//               fontSize: "13px",
//               fontWeight: 600,
//               lineHeight: 1,
//             }}
//           >
//             {record.department_count}
//           </span>
//         </div>
//       ),
//     },
//     {
//       title: getSearchableHeader(
//         "Program Code",
//         "program_code",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "program_code",
//       key: "program_code",
//       sorter: (a, b) => a.program_code.localeCompare(b.program_code),
//       render: displayValue,
//     },
//     {
//       title: getSearchableHeader(
//         "Location",
//         "location_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "location_name",
//       key: "location_name",
//       sorter: (a, b) => a.location_name.localeCompare(b.location_name),
//       render: displayValue,
//       onCell: () => cellWidth(180),
//     },
//     {
//       title: getSearchableHeader(
//         "Created On",
//         "created_at",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "created_at",
//       key: "created_at",
//       render: (value) => formatDate(value),
//       sorter: (a, b) => a.created_at.localeCompare(b.created_at),
//     },
//     {
//       title: getSearchableHeader(
//         "Last Updated",
//         "updated_at",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "updated_at",
//       key: "updated_at",
//       render: (value) => formatDate(value),
//       sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
//     },
//     {
//       title: "Status",
//       key: "status",
//       align: "center",
//       render: (_: any, record: any) => (
//         <div className="form-check form-switch d-flex justify-content-center">
//           <input
//             className="form-check-input"
//             type="checkbox"
//             checked={record.is_active}
//             onChange={() => handleStatusToggle(record)}
//           />
//         </div>
//       ),
//     },
//   ];

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {/* Breadcrumb */}
//         <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//           <div className="my-auto mb-2">
//             <h2 className="mb-1">Program Setup</h2>
//             <nav>
//               <ol className="breadcrumb mb-0">
//                 <li className="breadcrumb-item">
//                   <Link to={all_routes.superAdminDashboard}>
//                     <i className="ti ti-smart-home" />
//                   </Link>
//                 </li>
//                 <li className="breadcrumb-item">Account Activation</li>
//                 <li className="breadcrumb-item">Account Configuration</li>
//                 <li className="breadcrumb-item active">Program Setup</li>
//               </ol>
//             </nav>
//           </div>
//           <div className="head-icons ms-2">
//             <CollapseHeader />
//           </div>
//         </div>

//         <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//           <button
//             className="btn btn-primary"
//             onClick={() => navigate(all_routes.addprogram)}
//           >
//             <i className="ti ti-plus"></i> Add Program
//           </button>
//           <button
//             className="btn border bg-white px-3"
//             onClick={() => setShowGuide((prev) => !prev)}
//           >
//             <i className="ti ti-info-circle-filled fs-16"></i> Guide
//           </button>
//         </div>

//         {showGuide && (
//           <div
//             style={{
//               overflow: "hidden",
//               maxHeight: showGuide ? "500px" : "0px",
//               transition: "all 0.4s ease",
//             }}
//           >
//             {/* Triangle Arrow */}
//             <div className="text-end pe-3">
//               <i
//                 className="ti ti-triangle-inverted-filled"
//                 style={{
//                   fontSize: "22px",
//                   color: "#cfcfcf",
//                   lineHeight: 0,
//                 }}
//               ></i>
//             </div>

//             {/* Guide Card */}
//             <div
//               className="card border-0 shadow-sm"
//               style={{
//                 borderRadius: "4px",
//               }}
//             >
//               <div className="card-body position-relative p-4">
//                 {/* Close Icon */}
//                 <div className="position-absolute top-0 end-0 mt-3 me-3">
//                   <i
//                     className="ti ti-x"
//                     style={{
//                       background: "#6c757d",
//                       color: "#fff",
//                       borderRadius: "50%",
//                       width: "20px",
//                       height: "20px",
//                       fontSize: "12px",
//                       cursor: "pointer",
//                       display: "inline-flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                     onClick={() => setShowGuide(false)}
//                   ></i>
//                 </div>

//                 {/* Content */}
//                 <h5 className="fw-bold mb-3">Program Setup</h5>

//                 <ul
//                   className="mb-0 ps-3 text-dark"
//                   style={{ lineHeight: "1.8", listStyleType: "disc" }}
//                 >
//                   <li>
//                     Create and manage programs to organize candidate screening
//                     requirements by job role, department, or location. Programs
//                     help you assign appropriate screening packages and maintain
//                     consistent verification processes.
//                   </li>
//                   <li>
//                     For example, you can create separate programs for IT, BPO,
//                     lateral hiring, freshers, or different office locations.
//                   </li>
//                   <li>
//                     Select a brand to view and manage its programs. Configured
//                     programs will be available for selection during candidate
//                     onboarding.
//                   </li>
//                 </ul>
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="col-md-4">
//           <SearchableSelect
//             label="Brand Name"
//             name="project_id"
//             value={selectedProjectId}
//             options={projects.map((item: any) => ({
//               label: item.project_name,
//               value: item.project_id,
//             }))}
//             onChange={(_, value) => {
//               setSelectedProjectId(value);
//             }}
//             hasRowGrid={false}
//             required
//           />
//         </div>
//         {/* Table  */}
//         {selectedProjectId &&
//           (programLoading ? (
//             <SnipperLoader />
//           ) : (
//             <div className="card mt-3">
//               {/* <Datatable
//                 dataSource={filteredData}
//                 columns={columns}
//                 Selection={false}
//               /> */}
//               <Datatable
//                 dataSource={filteredData}
//                 columns={columns}
//                 Selection={false}
//                 fixedHeader
//                 tableHeight={650}
//                 pagination={{
//                   current: pagination.page,
//                   pageSize: pagination.limit,
//                   total,
//                 }}
//                 handleTableChange={(p: any) =>
//                   handleTableChange(p.current, p.pageSize)
//                 }
//               />
//             </div>
//           ))}
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default ProgramList;
